import { normalizeContextFieldDefinitions } from '@/utils/contexts'
import { formatTypedFieldDisplay } from '@/utils/leadFieldTypes'

/** API / lead-status keys that are not context data columns. */
export const STATUS_API_FIELD_KEYS = new Set(['request_status', 'status'])

/**
 * Build display field defs from a context/org definitions map.
 * Skips disabled fields, status-update API keys, and (by default) internal
 * fields -- system-managed values like a reminder anchor date, which stay
 * out of every lead table/form except bulk-upload column mapping (that
 * reads custom_field_definitions directly, never through this builder).
 * Pass includeInternal: true for the one caller that legitimately still
 * needs them (none today -- kept as an escape hatch, not a default).
 */
export function buildFieldDefsFromDefinitions(definitions = {}, { includeInternal = false } = {}) {
  const source = normalizeContextFieldDefinitions(definitions)
  return Object.entries(source)
    .filter(([key, definition]) => {
      if (STATUS_API_FIELD_KEYS.has(String(key || '').trim())) return false
      if (!includeInternal && definition?.is_internal === true) return false
      return definition?.enabled !== false
    })
    .map(([key, definition]) => {
      const type = String(definition?.type ?? 'TEXT').trim().toUpperCase()
      const options = Array.isArray(definition?.options) ? definition.options : []
      const label = definition.label || key.replace(/_/g, ' ')
      const isLongText =
        type === 'TEXT' &&
        /message|note|requirement|pain|description|overview/i.test(
          `${key} ${label}`,
        )
      return {
        key,
        label,
        type,
        options,
        long: Boolean(isLongText),
        placeholder:
          type === 'PHONE_NUMBER'
            ? '+91-9876543210'
            : type === 'DATE'
              ? 'DD/MM/YYYY'
              : type === 'TIME'
                ? 'HH:MM'
                : label,
        locked: definition.locked === true,
        isSystem: definition.is_system === true,
        isInternal: definition.is_internal === true,
      }
    })
}

export function cleanLeadDisplay(value) {
  const text = String(value ?? '').trim()
  if (!text || text === '—' || text === '--' || text === '-') return ''
  if (/^unknown(\b.*)?$/i.test(text)) return ''
  const parts = text.split(/\s+/).filter(Boolean)
  if (parts.length >= 2 && parts.length % 2 === 0) {
    const mid = parts.length / 2
    const left = parts.slice(0, mid).join(' ')
    const right = parts.slice(mid).join(' ')
    if (left.toLowerCase() === right.toLowerCase()) return left
  }
  return text
}

/** True for Cognito / request UUIDs — never show these as people or labels. */
export function isLikelyUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(value ?? '').trim(),
  )
}

/** First readable label from candidates; skips empty values and UUIDs. */
export function humanDisplayLabel(...values) {
  for (const value of values) {
    const text = cleanLeadDisplay(value)
    if (text && !isLikelyUuid(text)) return text
  }
  return ''
}

/**
 * Resolve a product field value from a normalized lead / business-query record.
 * Returns the raw stored value (epoch for DATE, etc.).
 */
export function resolveLeadFieldValue(lead, key) {
  if (!lead || !key) return ''
  const custom =
    lead.customFields && typeof lead.customFields === 'object'
      ? lead.customFields
      : {}
  const raw = lead.raw && typeof lead.raw === 'object' ? lead.raw : {}

  switch (key) {
    case 'contact_name':
      return (
        cleanLeadDisplay(lead.name) ||
        cleanLeadDisplay(raw.contact_name) ||
        cleanLeadDisplay(custom.contact_name)
      )
    case 'contact_email':
      return (
        cleanLeadDisplay(lead.email) ||
        cleanLeadDisplay(raw.contact_email) ||
        cleanLeadDisplay(custom.contact_email)
      )
    case 'contact_number':
      return String(
        lead.phoneRaw ||
          lead.phone ||
          raw.contact_number ||
          custom.contact_number ||
          '',
      ).trim()
    case 'type_of_business':
      return (
        cleanLeadDisplay(lead.typeOfBusiness) ||
        cleanLeadDisplay(raw.type_of_business) ||
        cleanLeadDisplay(custom.type_of_business)
      )
    case 'industry':
      return (
        cleanLeadDisplay(lead.industry) ||
        cleanLeadDisplay(raw.industry) ||
        cleanLeadDisplay(custom.industry)
      )
    case 'message':
      return (
        cleanLeadDisplay(lead.message) ||
        cleanLeadDisplay(raw.message) ||
        cleanLeadDisplay(custom.message)
      )
    case 'comments':
      return (
        cleanLeadDisplay(lead.comments) ||
        cleanLeadDisplay(raw.comments) ||
        cleanLeadDisplay(custom.comments)
      )
    default: {
      const value = custom[key] ?? raw[key] ?? lead[key]
      if (value == null || value === '') return ''
      if (typeof value === 'number') return value
      return cleanLeadDisplay(value) || value
    }
  }
}

/** Resolve + format for table / read-only UI. */
export function resolveLeadFieldDisplay(lead, key, type) {
  const raw = resolveLeadFieldValue(lead, key)
  if (raw === '' || raw == null) return ''
  const typed = formatTypedFieldDisplay(raw, type)
  return typed || cleanLeadDisplay(raw) || String(raw)
}

/** CSS grid track for a product field column (shrinkable so Actions stays visible). */
export function leadFieldColumnTrack(field) {
  if (!field) return 'minmax(0, 1fr)'
  if (field.key === 'contact_name') return 'minmax(5.5rem, 1.25fr)'
  if (
    field.key === 'contact_email' ||
    field.key === 'contact_number' ||
    field.type === 'PHONE_NUMBER'
  ) {
    return 'minmax(5rem, 1.1fr)'
  }
  if (field.type === 'DATE') return 'minmax(4.5rem, 0.85fr)'
  if (field.long) return 'minmax(5rem, 1.2fr)'
  return 'minmax(4.5rem, 1fr)'
}

/**
 * Build grid-template-columns for the leads table:
 * checkbox + dynamic product fields + status + submitted + actions.
 * Field tracks may shrink; Actions stays a fixed trailing column.
 */
export function buildLeadsTableGridColumns(fieldDefs = []) {
  const fieldTracks = fieldDefs.map(leadFieldColumnTrack)
  return [
    '28px',
    ...fieldTracks,
    'minmax(5.5rem, max-content)',
    'minmax(6rem, 0.85fr)',
    '56px',
  ].join(' ')
}
